// Rewritten on Niobe's tokens from Dealtracker src/appframe/tours/HighlightRing.tsx (commit 30ca0cb).
// `useAnchorRect` is unchanged; the look (brand ring, surface halo, pulse, forced colours) is the
// `.tour-ring` class in styles.css.
import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/** The ring's and the step window's border: 4px of the brand token, in every colour scheme. */
export const RING_WIDTH = 4
/** The surface-coloured halo on each side of the ring. */
const HALO_WIDTH = 2
/** The ring and its inner halo sit wholly outside the anchor: its own border stays visible. */
const OFFSET = RING_WIDTH + HALO_WIDTH

export type Rect = { top: number; left: number; width: number; height: number }

function same(a: Rect | null, b: Rect): boolean {
  return !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
}

/**
 * The anchor's viewport rectangle, kept current on scroll, resize and layout changes
 * (a panel resize, a list reflow). Re-renders only when it actually changes.
 */
export function useAnchorRect(anchor: HTMLElement | null): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null)

  useLayoutEffect(() => {
    if (!anchor) { setRect(null); return }
    let frame = 0
    const update = () => setRect((prev) => {
      const r = anchor.getBoundingClientRect()
      const next = { top: r.top, left: r.left, width: r.width, height: r.height }
      return same(prev, next) ? prev : next
    })
    update()
    // A per-frame check catches every way the anchor can move, including CSS
    // transitions that fire no scroll or resize event.
    const loop = () => { update(); frame = requestAnimationFrame(loop) }
    if (typeof requestAnimationFrame === 'function') frame = requestAnimationFrame(loop)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])

  return rect
}

/**
 * Rings the step's anchor: a 4px `--primary` line 6px outside it, with a 2px `--surface` halo on
 * both sides, so it keeps a ≥ 3:1 non-text contrast whatever lies behind. Nothing is dimmed and the
 * ring is `pointer-events: none`: the page stays clickable — the user performs every action. It
 * pulses twice on step entry (it re-mounts with each step), not under reduced motion.
 */
export function HighlightRing({ anchor }: { anchor: HTMLElement }) {
  const rect = useAnchorRect(anchor)
  if (!rect) return null
  return createPortal(
    <div
      aria-hidden="true"
      data-testid="tour-highlight"
      data-tour-window=""
      className="tour-ring"
      style={{
        top: rect.top - OFFSET, left: rect.left - OFFSET,
        width: rect.width + 2 * OFFSET, height: rect.height + 2 * OFFSET,
      }}
    />,
    document.body,
  )
}
